'use client';

import * as React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { InterviewData } from '@/lib/interviewTypes';
import { getStatusBadgeVariant } from '@/lib/interviewUtils';
import { useSelector } from 'react-redux';
import { RootState } from '@/lib/userType';
import { useStudentTakeInterviewMutation, useGetInterviewAttemptsQuery } from '@/store/instructor/interview';
import { toast } from 'sonner';
import InterviewCardHeader from './InterviewCardHeader';
import InterviewCardSchedule from './InterviewCardSchedule';
import InterviewCardInstructorActions from './InterviewCardInstructorActions';
import InterviewCardStudentSection from './InterviewCardStudentSection';
import InterviewCardInstructorAgreedList, { InstructorAttempt } from './InterviewCardInstructorAgreedList';

interface InterviewCardProps {
    interview: InterviewData;
    showInstructor?: boolean;
}

const InterviewCard: React.FC<InterviewCardProps> = ({
    interview,
    showInstructor = false,
}) => {
    const scheduledDate = new Date(interview.scheduledDate);
    const now = new Date();
    const { user } = useSelector((state: RootState) => state.user);

    const [studentTakeInterview, { isLoading: isTaking }] = useStudentTakeInterviewMutation();

    const isMissed = interview.status === 'scheduled' && scheduledDate < now;
    const displayStatus = isMissed ? 'missed' : interview.status;
    const badgeVariant = getStatusBadgeVariant(displayStatus);

    const { data: attemptsData } = useGetInterviewAttemptsQuery(
        { interviewId: interview._id, Token: user?.token },
        { skip: !user?.token }
    );

    const attemptsList = React.useMemo(() => {
        const raw = (attemptsData as any)?.data ?? attemptsData;
        return Array.isArray(raw) ? raw : [];
    }, [attemptsData]);

    const hasAgreed = React.useMemo(() => {
        if (!user?._id) return false;
        return attemptsList.some((attempt: any) => {
            const sid = attempt?.studentId?._id || attempt?.studentId || attempt?.student?._id;
            return String(sid) === String(user._id);
        });
    }, [attemptsList, user]);

    const agreedAttempts = React.useMemo<InstructorAttempt[]>(() => {
        return attemptsList
            .filter((attempt: any) => (attempt?.status ? ['scheduled', 'completed'].includes(attempt.status) : true))
            .map((attempt: any) => {
                const student = attempt?.student || attempt?.studentId || {};
                const name = student?.fname && student?.lname
                    ? `${student.fname} ${student.lname}`
                    : (student?.name || student?.email || 'Unnamed Student');

                return {
                    attemptId: attempt?._id,
                    status: attempt?.status,
                    name,
                    email: student?.email,
                    studentId: student?._id || attempt?.studentId,
                    technicalScore: attempt?.technicalScore,
                    communicationScore: attempt?.communicationScore,
                    problemSolvingScore: attempt?.problemSolvingScore,
                    feedback: attempt?.feedback,
                    overallScore: attempt?.overallScore,
                };
            });
    }, [attemptsList]);

    const getBorderColor = () => {
        if (isMissed) return 'border-l-red-500';
        switch (interview.status) {
            case 'completed':
                return 'border-l-emerald-500';
            case 'scheduled':
                return 'border-l-blue-500';
            case 'missed':
                return 'border-l-red-500';
            case 'cancelled':
                return 'border-l-yellow-500';
            default:
                return 'border-l-gray-300';
        }
    };

    const handleStudentAgree = async () => {
        if (!user?._id || !user?.token) {
            toast.error('You must be logged in to agree');
            throw new Error('Missing user credentials');
        }

        await studentTakeInterview({
            interviewId: interview._id,
            Token: user.token,
            formData: { studentId: user._id },
        }).unwrap();
    };

    return (
        <Card className={`border-l-4 bg-white shadow-sm dark:bg-gray-100 dark:text-black${getBorderColor()} dark:bg-black dark:text-white`}>
            <CardContent className="space-y-4 p-6">
                <InterviewCardHeader
                    moduleName={interview.moduleName}
                    statusLabel={displayStatus}
                    badgeVariant={badgeVariant}
                    actions={
                        user?.role === 'instructor' && interview.status === 'scheduled'
                            ? (
                                <InterviewCardInstructorActions
                                    interview={interview}
                                    token={user?.token}
                                />
                            )
                            : undefined
                    }
                />

                <InterviewCardSchedule scheduledDate={scheduledDate} />

                {user?.role === 'student' ? (
                    <InterviewCardStudentSection
                        interview={interview}
                        isMissed={isMissed}
                        hasAgreed={hasAgreed}
                        isAgreeing={isTaking}
                        onAgree={async () => {
                            await handleStudentAgree();
                            toast.success('You have agreed to attend this interview');
                        }}
                    />
                ) : null}

                {user?.role === 'instructor' && agreedAttempts.length > 0 ? (
                    <InterviewCardInstructorAgreedList
                        attempts={agreedAttempts}
                        token={user?.token}
                        instructorId={(user as any)?._id || (user as any)?.id}
                    />
                ) : null}

                {showInstructor && (interview as any).conductedBy ? (
                    <div className="rounded-md border border-gray-100 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                        <strong>Conducted by:</strong>{' '}
                        {(interview as any).conductedBy?.name ||
                            (interview as any).conductedBy?.email ||
                            'N/A'}
                            </div>
                     ) : null}
                    </CardContent>
                    </Card>
    );
};

export default InterviewCard;

